import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { useForgotPasswordMutation } from '../../application/authMutations';

interface ForgotPasswordModalProps {
  initialEmail: string;
  onClose: () => void;
}

export function ForgotPasswordModal({ initialEmail, onClose }: ForgotPasswordModalProps) {
  const { C, T } = useTheme();
  const { t } = useTranslation('common');
  const [email, setEmail] = useState(initialEmail);
  const [focused, setFocused] = useState(false);
  const [sent, setSent] = useState(false);
  const mutation = useForgotPasswordMutation();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (mutation.isPending || !email.trim()) return;
    mutation.mutate({ email }, { onSuccess: () => { setSent(true); } });
  };

  return (
    <div
      className="mi-anim-fade-in"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: T.modalBg,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        className="mi-anim-modal-in"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 357,
          background: T.surfaceBg,
          borderRadius: 16,
          boxShadow: T.shadow,
          border: `1px solid ${T.border}`,
          padding: '24px 27px 20px',
          position: 'relative',
        }}
      >
        {/* close button */}
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 24,
            height: 24,
            borderRadius: 7,
            border: 'none',
            background: `${C.danger}12`,
            color: C.danger,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background .15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = `${C.danger}22`; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = `${C.danger}12`; }}
        >
          <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {!sent ? (
          <form
            onSubmit={handleSubmit}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              minHeight: 296,
            }}
          >
            {/* lock icon */}
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: `${C.blue}15`,
                color: C.blue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 15,
              }}
            >
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 9.9-1" />
              </svg>
            </div>

            <h2 style={{ fontSize: 17, fontWeight: 800, color: T.clockText, margin: '0 0 7px', letterSpacing: '-.01em' }}>
              {t('forgotPassword.title')}
            </h2>
            <p style={{ fontSize: 11, color: T.textMuted, lineHeight: 1.6, marginBottom: 18, maxWidth: 272 }}>
              {t('forgotPassword.description')}
            </p>

            <label style={{ width: '100%', textAlign: 'left', fontSize: 10, fontWeight: 700, color: T.text, marginBottom: 8, display: 'block' }}>
              {t('forgotPassword.emailLabel')}
            </label>
            <div style={{ position: 'relative', marginBottom: 20, width: '100%' }}>
              <svg
                width={13} height={13} viewBox="0 0 24 24" fill="none"
                stroke={focused ? C.blue : T.textMuted}
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', transition: 'stroke .15s' }}
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
              </svg>
              <input
                type="email"
                autoFocus
                value={email}
                onChange={(e) => { setEmail(e.target.value); }}
                onFocus={() => { setFocused(true); }}
                onBlur={() => { setFocused(false); }}
                placeholder={t('forgotPassword.emailPlaceholder')}
                style={{
                  width: '100%',
                  padding: '11px 12px 11px 34px',
                  borderRadius: 9,
                  border: `1.5px solid ${focused ? C.blue : T.border}`,
                  background: T.bg,
                  color: T.text,
                  fontFamily: 'inherit',
                  fontSize: 12,
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color .15s, box-shadow .15s',
                  boxShadow: focused ? `0 0 0 4px ${C.blue}18` : 'none',
                }}
              />
            </div>

            {mutation.isError && (
              <p style={{ fontSize: 11, color: C.danger, marginBottom: 10, alignSelf: 'flex-start' }}>
                {t('forgotPassword.errorSend')}
              </p>
            )}

            <div style={{ display: 'flex', gap: 8, width: '100%', marginTop: 'auto' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: 9,
                  border: `1.5px solid ${T.border}`,
                  background: 'transparent',
                  color: T.text,
                  fontFamily: 'inherit',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background .15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = T.bg; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                {t('forgotPassword.cancel')}
              </button>
              <button
                type="submit"
                disabled={mutation.isPending || !email.trim()}
                style={{
                  flex: 1.4,
                  padding: '10px',
                  borderRadius: 9,
                  border: 'none',
                  background: (mutation.isPending || !email.trim()) ? `${C.blue}88` : C.blue,
                  color: '#fff',
                  fontFamily: 'inherit',
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: (mutation.isPending || !email.trim()) ? 'not-allowed' : 'pointer',
                  boxShadow: `0 5px 14px ${C.blue}35`,
                  transition: 'background .15s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 7,
                }}
              >
                {mutation.isPending && (
                  <svg width={11} height={11} viewBox="0 0 24 24" className="mi-anim-spin" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                )}
                {mutation.isPending ? t('forgotPassword.sending') : t('forgotPassword.sendInstructions')}
              </button>
            </div>
          </form>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              minHeight: 296,
              paddingTop: 3,
            }}
          >
            {/* success checkmark */}
            <div
              className="mi-anim-check-pop"
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: `${C.success}18`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 15,
              }}
            >
              <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={C.success} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>

            <h2 style={{ fontSize: 17, fontWeight: 800, color: T.clockText, margin: '0 0 7px', letterSpacing: '-.01em' }}>
              {t('forgotPassword.sentTitle')}
            </h2>
            <p style={{ fontSize: 11, color: T.textMuted, lineHeight: 1.6, marginBottom: 5 }}>
              {t('forgotPassword.sentDescription')}
            </p>
            <p style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 12, wordBreak: 'break-all' }}>
              {email}
            </p>
            <p style={{ fontSize: 10, color: T.textMuted, lineHeight: 1.6, marginBottom: 18, maxWidth: 272 }}>
              {t('forgotPassword.sentNote')}
            </p>

            <button
              type="button"
              onClick={onClose}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: 9,
                border: 'none',
                background: C.blue,
                color: '#fff',
                fontFamily: 'inherit',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: `0 5px 14px ${C.blue}35`,
                marginTop: 'auto',
              }}
            >
              {t('forgotPassword.understood')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
